import {
  createHotcrpCsv,
  createPersonalCsv,
  downloadCsv,
  selectTopInterestedPapers,
  validatePreferenceMapping,
} from './export';
import {
  auditChoices,
  type ChoiceAuditEntry,
  type ProfileSuggestion,
} from './choice-review';
import { parsePaperImport } from './import';
import { extractPdfText } from './pdf';
import { createProfileItem } from './profile';
import {
  applyPreferenceFeedback,
  boostedUnreviewedRankings,
  countSimilarPapers,
  describePreferenceFeedback,
  paperScorePresentation,
  proposedNegativePhrases,
  variantThresholdForSeed,
} from './ranking';
import {
  MAX_REVIEW_TARGET,
  REVIEW_STATUS_FILTERS,
  countReviewStatuses,
  normalizeReviewTarget,
  reviewProgressMessage,
  reviewStatusLabel,
} from './review-stats';
import {
  MAX_SIMILARITY_LIMIT,
  normalizeSimilarityLimit,
  projectEmbeddings2d,
  rankByPublicationCluster,
} from './similarity';
import {
  clearPersistedState,
  exportPersistedState,
  importPersistedState,
  loadPersistedState,
  savePersistedState,
} from './storage';
import { scorePaperTextMatch } from './text-search';
import type {
  AppState,
  EmbeddingDevice,
  EmbeddingModelId,
  Paper,
  PreferenceMapping,
  ProfileKind,
  RankingResult,
  ReviewStatus,
  WorkerRequest,
  WorkerResponse,
} from './types';

const CONSENT_KEY = 'paper-bidding:persistence-enabled';
const PDF_SUMMARY_ONLY_KEY = 'paper-bidding:pdf-summary-only';
const DEFAULT_RERANKER_CANDIDATE_COUNT = 50;
const MAX_RERANKER_CANDIDATE_COUNT = 200;
const DEFAULT_MAPPING: PreferenceMapping = {
  interested: 3,
  not_interested: -1,
  uncertain: 1,
};
const MODEL_DETAILS: Record<EmbeddingModelId, { label: string; description: string }> = {
  minilm: {
    label: 'MiniLM',
    description:
      'Fast bundled option. The quantized model is served with the app and works with WebGPU or WASM.',
  },
  specter2: {
    label: 'SPECTER2 proximity',
    description:
      'Default scientific-paper retrieval model. Downloads about 441 MB from Hugging Face, then embeds titles and abstracts locally.',
  },
  'bge-m3': {
    label: 'BGE-M3',
    description:
      'Large multilingual retrieval model. Downloads about 568 MB from Hugging Face, then embeds titles and abstracts locally.',
  },
  embeddinggemma: {
    label: 'EmbeddingGemma 300M',
    description:
      'Google’s 300M multilingual embedding model. Downloads about 1.24 GB of FP32 ONNX weights and requires WebGPU. Uses Gemma terms of use.',
  },
};

let state: AppState = {
  profileItems: [],
  papers: [],
  rankings: [],
  profileEmbeddings: {},
  profileBodyEmbeddings: {},
  paperEmbeddings: {},
  negativeKeywordEmbeddings: {},
  decisions: {},
  preferenceMapping: { ...DEFAULT_MAPPING },
  negativeKeywords: [],
  exportLimit: null,
  selectedPublicationIds: [],
  similarityLimit: 20,
  reviewTarget: null,
  persistenceEnabled: localStorage.getItem(CONSENT_KEY) !== 'false',
};

let embeddingWorker: Worker | null = null;
let activeRequestId: string | null = null;
let saveTimer: number | undefined;
let editingProfileId: string | null = null;
let pendingNotInterestedPaperId: string | null = null;
let hideVotedPapers = false;

function element<T extends Element>(id: string): T {
  const found = document.querySelector<T>(`#${id}`);
  if (!found) throw new Error(`Missing required element #${id}`);
  return found;
}

const profileForm = element<HTMLFormElement>('profile-form');
const profileKind = element<HTMLSelectElement>('profile-kind');
const profileLabel = element<HTMLInputElement>('profile-label');
const profileText = element<HTMLTextAreaElement>('profile-text');
const profileWeight = element<HTMLInputElement>('profile-weight');
const profileSubmit = element<HTMLButtonElement>('profile-submit');
const profileItems = element<HTMLDivElement>('profile-items');
const pdfInput = element<HTMLInputElement>('pdf-input');
const pdfSummaryOnly = element<HTMLInputElement>('pdf-summary-only');
const pdfStatus = element<HTMLSpanElement>('pdf-status');
const paperFile = element<HTMLInputElement>('paper-file');
const paperPaste = element<HTMLTextAreaElement>('paper-paste');
const importSummary = element<HTMLDivElement>('import-summary');
const importErrors = element<HTMLDivElement>('import-errors');
const rankButton = element<HTMLButtonElement>('rank-papers');
const cancelButton = element<HTMLButtonElement>('cancel-ranking');
const rankProgress = element<HTMLProgressElement>('rank-progress');
const rankStatus = element<HTMLSpanElement>('rank-status');
const runtimeLabel = element<HTMLParagraphElement>('runtime-label');
const embeddingModel = element<HTMLSelectElement>('embedding-model');
const modelDescription = element<HTMLParagraphElement>('model-description');
const useReranker = element<HTMLInputElement>('use-reranker');
const rerankerCandidateCount = element<HTMLInputElement>('reranker-candidate-count');
const rerankerEstimate = element<HTMLElement>('reranker-estimate');
const rerankerProgressWrap = element<HTMLDivElement>('reranker-progress-wrap');
const rerankerProgress = element<HTMLProgressElement>('reranker-progress');
const rerankerStatus = element<HTMLSpanElement>('reranker-status');
const reviewProgressSummary = element<HTMLParagraphElement>('review-progress-summary');
const reviewTarget = element<HTMLInputElement>('review-target');
const reviewProgressBarWrap = element<HTMLDivElement>('review-progress-bar-wrap');
const reviewTargetProgress = element<HTMLProgressElement>('review-target-progress');
const reviewStatusCounts = element<HTMLDivElement>('review-status-counts');
const filterSearch = element<HTMLInputElement>('filter-search');
const filterTopic = element<HTMLSelectElement>('filter-topic');
const filterTrack = element<HTMLSelectElement>('filter-track');
const filterStatus = element<HTMLSelectElement>('filter-status');
const filterScore = element<HTMLInputElement>('filter-score');
const scoreOutput = element<HTMLOutputElement>('score-output');
const sortResults = element<HTMLSelectElement>('sort-results');
const hideVotedPapersControl = element<HTMLInputElement>('hide-voted-papers');
const resultCount = element<HTMLParagraphElement>('result-count');
const resultList = element<HTMLDivElement>('result-list');
const choiceCheckStatus = element<HTMLDivElement>('choice-check-status');
const choiceCheckContent = element<HTMLDivElement>('choice-check-content');
const choiceCheckSummary = element<HTMLDivElement>('choice-check-summary');
const choiceReviewList = element<HTMLDivElement>('choice-review-list');
const choiceConsistentList = element<HTMLDivElement>('choice-consistent-list');
const choiceConsistentCount = element<HTMLElement>('choice-consistent-count');
const choiceAlternatives = element<HTMLDivElement>('choice-alternatives');
const profileSuggestions = element<HTMLDivElement>('profile-suggestions');
const similarityPublications = element<HTMLDivElement>('similarity-publications');
const similarityLimit = element<HTMLInputElement>('similarity-limit');
const similarityHideVotedPapers = element<HTMLInputElement>('similarity-hide-voted-papers');
const similarityStatus = element<HTMLDivElement>('similarity-status');
const similarityMap = element<SVGSVGElement>('similarity-map');
const similarityCount = element<HTMLParagraphElement>('similarity-count');
const similarityResults = element<HTMLDivElement>('similarity-results');
const negativeKeywordForm = element<HTMLFormElement>('negative-keyword-form');
const negativeKeyword = element<HTMLInputElement>('negative-keyword');
const negativeKeywords = element<HTMLDivElement>('negative-keywords');
const persistenceToggle = element<HTMLInputElement>('persistence-toggle');
const persistenceStatus = element<HTMLParagraphElement>('persistence-status');
const sessionImport = element<HTMLInputElement>('import-session');
const mapInterested = element<HTMLInputElement>('map-interested');
const mapNotInterested = element<HTMLInputElement>('map-not-interested');
const mapUncertain = element<HTMLInputElement>('map-uncertain');
const exportStatus = element<HTMLParagraphElement>('export-status');
const limitExport = element<HTMLInputElement>('limit-export');
const exportLimit = element<HTMLInputElement>('export-limit');
const confirmDialog = element<HTMLDialogElement>('confirm-dialog');
const notInterestedDialog = element<HTMLDialogElement>('not-interested-dialog');
const boostedPapersDialog = element<HTMLDialogElement>('boosted-papers-dialog');
const boostedPapersSummary = element<HTMLParagraphElement>('boosted-papers-summary');
const boostedPapersList = element<HTMLDivElement>('boosted-papers-list');
const notInterestedForm = element<HTMLFormElement>('not-interested-form');
const notInterestedSummary = element<HTMLParagraphElement>('not-interested-summary');
const notInterestedPenalizeSimilar = element<HTMLInputElement>('not-interested-penalize-similar');
const notInterestedSimilarHint = element<HTMLElement>('not-interested-similar-hint');
const notInterestedSuggestions = element<HTMLFieldSetElement>('not-interested-suggestions');
const notInterestedSuggestionsEmpty = element<HTMLParagraphElement>(
  'not-interested-suggestions-empty',
);
const notInterestedSuggestionList = element<HTMLDivElement>('not-interested-suggestion-list');
const notInterestedManualKeywords = element<HTMLTextAreaElement>('not-interested-manual-keywords');
pdfSummaryOnly.checked = localStorage.getItem(PDF_SUMMARY_ONLY_KEY) === 'true';

function setText(target: HTMLElement, text: string): void {
  target.textContent = text;
}

async function detectPreferredEmbeddingDevice(): Promise<EmbeddingDevice> {
  if (!('gpu' in navigator)) return 'wasm';
  const gpuNavigator = navigator as Navigator & {
    gpu: { requestAdapter: () => Promise<unknown | null> };
  };
  try {
    return (await gpuNavigator.gpu.requestAdapter()) === null ? 'wasm' : 'webgpu';
  } catch {
    return 'wasm';
  }
}

const preferredEmbeddingDevice = detectPreferredEmbeddingDevice();

function updateRuntimeLabel(): void {
  if (selectedEmbeddingModel() === 'embeddinggemma') {
    setText(runtimeLabel, 'Checking WebGPU support for EmbeddingGemma…');
    void preferredEmbeddingDevice.then((device) => {
      if (selectedEmbeddingModel() !== 'embeddinggemma') return;
      setText(
        runtimeLabel,
        device === 'webgpu'
          ? 'This browser will run EmbeddingGemma on WebGPU with FP32 weights.'
          : 'EmbeddingGemma requires WebGPU; use MiniLM in this browser.',
      );
    });
    return;
  }
  setText(runtimeLabel, 'Checking which backend this browser will use…');
  void preferredEmbeddingDevice.then((device) => {
    setText(
      runtimeLabel,
      device === 'webgpu'
        ? 'This browser will use WebGPU.'
        : 'WebGPU is not supported in this browser; falling back to WASM.',
    );
  });
}

function selectedEmbeddingModel(): EmbeddingModelId {
  switch (embeddingModel.value) {
    case 'minilm':
    case 'bge-m3':
    case 'embeddinggemma':
      return embeddingModel.value;
    default:
      return 'specter2';
  }
}

function selectedRerankerCandidateCount(): number {
  const value = Number(rerankerCandidateCount.value);
  return Number.isFinite(value)
    ? Math.max(1, Math.min(MAX_RERANKER_CANDIDATE_COUNT, Math.floor(value)))
    : DEFAULT_RERANKER_CANDIDATE_COUNT;
}

function button(text: string, className: string): HTMLButtonElement {
  const node = document.createElement('button');
  node.type = 'button';
  node.className = className;
  node.textContent = text;
  return node;
}

function downloadJson(source: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([source], { type: 'application/json;charset=utf-8' }));
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

function scheduleSave(): void {
  if (!state.persistenceEnabled) return;
  window.clearTimeout(saveTimer);
  setText(persistenceStatus, 'Saving locally…');
  saveTimer = window.setTimeout(() => {
    void savePersistedState(state, true)
      .then(() => setText(persistenceStatus, 'Saved locally in this browser.'))
      .catch((error: unknown) => {
        setText(
          persistenceStatus,
          `Could not save locally: ${error instanceof Error ? error.message : String(error)}`,
        );
      });
  }, 250);
}

function renderProfile(): void {
  profileItems.replaceChildren();
  if (state.profileItems.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'muted';
    empty.textContent = 'No expertise items yet. Add at least one item before ranking.';
    profileItems.append(empty);
    return;
  }

  const fragment = document.createDocumentFragment();
  for (const item of state.profileItems) {
    const card = document.createElement('article');
    card.className = 'profile-item';

    const icon = document.createElement('span');
    icon.className = 'profile-icon';
    icon.textContent = item.kind.slice(0, 3);

    const content = document.createElement('div');
    const title = document.createElement('h3');
    title.textContent = `${item.label} · weight ${item.weight.toFixed(1)}`;
    const text = document.createElement('p');
    text.textContent = item.text;
    content.append(title, text);

    const actions = document.createElement('div');
    actions.className = 'profile-actions';
    const edit = button('Edit', 'icon-button text-button');
    edit.dataset.editProfile = item.id;
    edit.setAttribute('aria-label', `Edit ${item.label}`);
    const remove = button('×', 'icon-button');
    remove.dataset.removeProfile = item.id;
    remove.setAttribute('aria-label', `Remove ${item.label}`);
    actions.append(edit, remove);
    card.append(icon, content, actions);
    fragment.append(card);
  }
  profileItems.append(fragment);
}

function updateFilterOptions(): void {
  const selectedTopic = filterTopic.value;
  const selectedTrack = filterTrack.value;
  const topics = [...new Set(state.papers.flatMap((paper) => paper.topics))].sort();
  const tracks = [...new Set(state.papers.map((paper) => paper.track).filter(Boolean))].sort();

  filterTopic.replaceChildren(new Option('All topics', ''));
  filterTrack.replaceChildren(new Option('All tracks', ''));
  for (const topic of topics) filterTopic.add(new Option(topic, topic));
  for (const track of tracks) filterTrack.add(new Option(track, track));
  if (topics.includes(selectedTopic)) filterTopic.value = selectedTopic;
  if (tracks.includes(selectedTrack)) filterTrack.value = selectedTrack;
}

function addNegativeKeywords(phrases: readonly string[]): boolean {
  const next = [...state.negativeKeywords];
  let added = false;
  for (const raw of phrases) {
    const keyword = raw.trim().toLocaleLowerCase();
    if (!keyword || next.includes(keyword)) continue;
    next.push(keyword);
    added = true;
  }
  if (!added) return false;
  state.negativeKeywords = next;
  return true;
}

function parseManualPhrases(source: string): string[] {
  return source
    .split(/\r?\n|,/)
    .map((phrase) => phrase.trim())
    .filter((phrase) => phrase.length > 0);
}

function similarPenaltyHint(paperId: string): string {
  const seed = state.paperEmbeddings[paperId];
  const rankedCount = Object.keys(state.paperEmbeddings).length;
  const threshold = seed
    ? variantThresholdForSeed(paperId, seed, state.paperEmbeddings)
    : 0.8;
  const thresholdLabel = threshold.toFixed(2);
  if (!seed || rankedCount === 0) {
    return (
      `Downrank papers with cosine similarity ≥ ${thresholdLabel} to this one — typically close variants, ` +
      'not the whole topic. The bar is calibrated from the current paper list after ranking.'
    );
  }
  const similarCount = countSimilarPapers(paperId, seed, state.paperEmbeddings, threshold);
  const remaining = Math.max(0, rankedCount - 1);
  const countLabel =
    similarCount === 0
      ? `None of the other ${remaining} ranked papers currently meet this bar.`
      : `${similarCount} of ${remaining} other ranked paper${remaining === 1 ? '' : 's'} currently meet this bar.`;
  return (
    `Downrank papers with cosine similarity ≥ ${thresholdLabel} to this one — typically close variants, ` +
    `not the whole topic. ${countLabel}`
  );
}

function openNotInterestedDialog(paperId: string): void {
  const paper = state.papers.find((candidate) => candidate.id === paperId);
  if (!paper) return;
  pendingNotInterestedPaperId = paperId;
  notInterestedDialog.returnValue = '';
  notInterestedForm.reset();
  notInterestedSummary.textContent =
    `Paper ${paper.id} “${paper.title}” will be marked not interested. Optionally steer the rest of the list:`;
  notInterestedSimilarHint.textContent = similarPenaltyHint(paperId);
  const proposals = proposedNegativePhrases(paper, state.negativeKeywords);
  notInterestedSuggestionList.replaceChildren();
  notInterestedSuggestionsEmpty.hidden = proposals.length > 0;
  for (const phrase of proposals) {
    const option = document.createElement('label');
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.name = 'suggested-negative';
    checkbox.value = phrase;
    option.append(checkbox, document.createTextNode(phrase));
    notInterestedSuggestionList.append(option);
  }
  notInterestedSuggestions.hidden = false;
  notInterestedDialog.showModal();
}

notInterestedDialog.addEventListener('close', () => {
  const paperId = pendingNotInterestedPaperId;
  const shouldApply = notInterestedDialog.returnValue === 'apply';
  const penalizeSimilar = notInterestedPenalizeSimilar.checked;
  const phrases = [
    ...notInterestedSuggestionList.querySelectorAll<HTMLInputElement>('input:checked'),
  ]
    .map((input) => input.value)
    .concat(parseManualPhrases(notInterestedManualKeywords.value));
  pendingNotInterestedPaperId = null;
  if (!shouldApply || !paperId) return;
  applyNotInterestedDecision(paperId, penalizeSimilar, phrases);
});

function applyNotInterestedDecision(
  paperId: string,
  penalizeSimilar: boolean,
  phrases: readonly string[],
): void {
  const current = state.decisions[paperId] ?? { note: '' };
  const next = { ...current, status: 'not_interested' as const };
  if (penalizeSimilar) next.penalizeSimilar = true;
  else delete next.penalizeSimilar;
  state.decisions[paperId] = next;
  const addedKeywords = addNegativeKeywords(phrases);
  renderNegativeKeywords();
  rerankWithFeedback();
  renderResults();
  renderSimilarity();
  renderChoiceCheck();
  if (boostedPapersDialog.open) openBoostedPapersDialog();
  scheduleSave();
  if (addedKeywords && state.profileItems.length > 0 && state.papers.length > 0) {
    startRanking();
  }
}

function openBoostedPapersDialog(): void {
  const boosted = boostedUnreviewedRankings(state.rankings, state.decisions);
  boostedPapersList.replaceChildren();
  setText(
    boostedPapersSummary,
    boosted.length === 0
      ? 'No other unreviewed papers received a positive boost.'
      : `${boosted.length} unreviewed paper${boosted.length === 1 ? '' : 's'} received a positive similarity boost. Papers you already voted on are excluded.`,
  );

  for (const ranking of boosted) {
    const paper = state.papers.find((candidate) => candidate.id === ranking.paperId);
    if (!paper) continue;
    const row = document.createElement('article');
    row.className = 'boosted-paper';
    const heading = document.createElement('div');
    heading.className = 'boosted-paper-heading';
    const title = document.createElement('h3');
    title.textContent = paper.title;
    const details = document.createElement('small');
    const choice = state.rankings.findIndex((candidate) => candidate.paperId === paper.id) + 1;
    const positiveBoost = ranking.feedbackBreakdown?.positiveBoost ?? 0;
    details.textContent =
      `Choice #${choice} · Paper ${paper.id} · boosted +${positiveBoost.toFixed(3)}`;
    heading.append(title, details);
    const abstract = document.createElement('p');
    abstract.className = 'boosted-paper-abstract';
    abstract.textContent = excerpt(paper.abstract);
    row.append(heading, abstract);
    if (paper.abstract.length > 420) {
      const expansion = document.createElement('details');
      const summary = document.createElement('summary');
      summary.textContent = 'Read full abstract';
      const fullAbstract = document.createElement('p');
      fullAbstract.className = 'boosted-paper-abstract';
      fullAbstract.textContent = paper.abstract;
      expansion.append(summary, fullAbstract);
      row.append(expansion);
    }
    row.append(decisionControls(paper.id));
    boostedPapersList.append(row);
  }

  if (!boostedPapersDialog.open) boostedPapersDialog.showModal();
}

function setDecisionStatus(paperId: string, selected: ReviewStatus): void {
  const current = state.decisions[paperId] ?? { note: '' };
  if (current.status === selected) {
    const next = { ...current };
    delete next.status;
    delete next.penalizeSimilar;
    state.decisions[paperId] = next;
    rerankWithFeedback();
    renderResults();
    renderSimilarity();
    renderChoiceCheck();
    if (boostedPapersDialog.open) openBoostedPapersDialog();
    scheduleSave();
    return;
  }
  if (selected === 'not_interested') {
    openNotInterestedDialog(paperId);
    return;
  }
  const next = { ...current, status: selected };
  delete next.penalizeSimilar;
  state.decisions[paperId] = next;
  rerankWithFeedback();
  renderResults();
  renderSimilarity();
  renderChoiceCheck();
  scheduleSave();
  if (selected === 'interested' || boostedPapersDialog.open) openBoostedPapersDialog();
}

function renderNegativeKeywords(): void {
  negativeKeywords.replaceChildren();
  if (state.negativeKeywords.length === 0) return;

  const label = document.createElement('strong');
  label.textContent = 'Negative penalties:';
  negativeKeywords.append(label);
  for (const keyword of state.negativeKeywords) {
    const remove = button(`${keyword} ×`, 'excluded-topic');
    remove.dataset.removeNegativeKeyword = keyword;
    remove.setAttribute('aria-label', `Remove negative penalty ${keyword}`);
    negativeKeywords.append(remove);
  }
}

function renderImport(): void {
  if (state.papers.length === 0) {
    setText(importSummary, 'No papers imported yet.');
  } else {
    const abstracts = state.papers.filter((paper) => paper.abstract.length > 0).length;
    setText(
      importSummary,
      `Imported ${state.papers.length} papers; ${abstracts} include abstracts. Existing rankings were cleared.`,
    );
  }
  updateFilterOptions();
}

function excerpt(text: string, length = 420): string {
  if (!text) return 'No abstract was included in the permitted export.';
  return text.length <= length ? text : `${text.slice(0, length).trimEnd()}…`;
}

function rankingRows(): Array<{ paper: Paper; ranking: RankingResult }> {
  const papersById = new Map(state.papers.map((paper) => [paper.id, paper]));
  const search = filterSearch.value.trim();
  const minimum = Number(filterScore.value);
  const requestedStatus = filterStatus.value;

  const rows = state.rankings
    .map((ranking) => {
      const paper = papersById.get(ranking.paperId);
      return {
        ranking,
        paper,
        searchScore: paper && search ? scorePaperTextMatch(paper, search) : 0,
      };
    })
    .filter(
      (
        row,
      ): row is { paper: Paper; ranking: RankingResult; searchScore: number } =>
        row.paper !== undefined,
    )
    .filter(({ paper, ranking, searchScore }) => {
      const status = state.decisions[paper.id]?.status ?? 'unreviewed';
      return (
        (!search || searchScore > 0) &&
        (!filterTopic.value || paper.topics.includes(filterTopic.value)) &&
        (!filterTrack.value || paper.track === filterTrack.value) &&
        (!requestedStatus || status === requestedStatus) &&
        (!hideVotedPapers || status === 'unreviewed') &&
        ranking.score >= minimum
      );
    });

  rows.sort((left, right) => {
    switch (sortResults.value) {
      case 'search-relevance':
        return search
          ? right.searchScore - left.searchScore ||
              right.ranking.score - left.ranking.score
          : right.ranking.score - left.ranking.score;
      case 'score-asc':
        return left.ranking.score - right.ranking.score;
      case 'paper-asc':
        return left.paper.id.localeCompare(right.paper.id, undefined, { numeric: true });
      case 'title-asc':
        return left.paper.title.localeCompare(right.paper.title);
      default:
        return right.ranking.score - left.ranking.score;
    }
  });
  return rows;
}

function statusButton(
  paperId: string,
  status: ReviewStatus,
  label: string,
  selected: boolean,
): HTMLButtonElement {
  const node = button(label, `status-button${selected ? ' selected' : ''}`);
  node.dataset.paperId = paperId;
  node.dataset.status = status;
  node.setAttribute('aria-pressed', String(selected));
  return node;
}

function renderReviewProgress(): void {
  const counts = countReviewStatuses(state.papers, state.decisions);
  setText(reviewProgressSummary, reviewProgressMessage(counts, state.reviewTarget));
  reviewTarget.max = MAX_REVIEW_TARGET.toString();
  reviewTarget.value = state.reviewTarget?.toString() ?? '';
  const reviewQuota = state.reviewTarget;
  reviewProgressBarWrap.hidden = reviewQuota === null;
  if (reviewQuota !== null) {
    reviewTargetProgress.max = reviewQuota;
    reviewTargetProgress.value = Math.min(counts.interested, reviewQuota);
  }
  reviewStatusCounts.replaceChildren();
  if (counts.total === 0) return;

  const fragment = document.createDocumentFragment();
  for (const status of REVIEW_STATUS_FILTERS) {
    const chip = button('', 'review-status-chip');
    chip.dataset.statusFilter = status;
    const selected = filterStatus.value === status;
    chip.setAttribute('aria-pressed', String(selected));
    chip.setAttribute(
      'aria-label',
      `Show ${reviewStatusLabel(status).toLocaleLowerCase()} papers (${counts[status]})`,
    );
    const label = document.createElement('span');
    label.textContent = reviewStatusLabel(status);
    const value = document.createElement('strong');
    value.textContent = counts[status].toString();
    chip.append(label, value);
    fragment.append(chip);
  }
  reviewStatusCounts.append(fragment);
}

function decisionControls(paperId: string): HTMLDivElement {
  const decision = state.decisions[paperId] ?? { note: '' };
  const controls = document.createElement('div');
  controls.className = 'decision-row';
  controls.append(
    statusButton(paperId, 'interested', 'Interested', decision.status === 'interested'),
    statusButton(
      paperId,
      'uncertain',
      'Would review if needed',
      decision.status === 'uncertain',
    ),
    statusButton(
      paperId,
      'not_interested',
      'Not interested',
      decision.status === 'not_interested',
    ),
    statusButton(paperId, 'conflict', 'Conflict', decision.status === 'conflict'),
  );
  const note = document.createElement('input');
  note.type = 'text';
  note.className = 'note-input';
  note.placeholder = 'Private note';
  note.value = decision.note;
  note.dataset.notePaper = paperId;
  note.setAttribute('aria-label', `Private note for paper ${paperId}`);
  controls.append(note);
  return controls;
}

function applyReviewStatus(
  element: HTMLElement | SVGElement,
  paperId: string,
): ReviewStatus | undefined {
  const status = state.decisions[paperId]?.status;
  if (status) element.dataset.reviewStatus = status;
  return status;
}

function renderResults(): void {
  renderReviewProgress();
  hideVotedPapersControl.checked = hideVotedPapers;
  const rows = rankingRows();
  const rankByPaper = new Map(
    state.rankings.map((ranking, index) => [ranking.paperId, index + 1]),
  );
  resultList.replaceChildren();
  renderNegativeKeywords();
  if (state.rankings.length === 0) {
    setText(resultCount, 'No rankings yet.');
    return;
  }
  setText(resultCount, `Showing ${rows.length} of ${state.rankings.length} ranked papers.`);

  const fragment = document.createDocumentFragment();
  for (const { paper, ranking } of rows) {
    const card = document.createElement('article');
    card.className = 'paper-card';
    card.dataset.resultPaper = paper.id;
    card.tabIndex = -1;
    applyReviewStatus(card, paper.id);
    card.id = `ranked-paper-${paper.id}`;

    const main = document.createElement('div');
    main.className = 'paper-main';
    const topline = document.createElement('div');
    topline.className = 'paper-topline';
    const rank = rankByPaper.get(paper.id);
    topline.textContent = `Choice #${rank ?? '—'} · Paper ${paper.id}${paper.track ? ` · ${paper.track}` : ''}`;
    const title = document.createElement('h3');
    title.textContent = paper.title;
    const abstract = document.createElement('p');
    abstract.className = 'paper-abstract';
    abstract.textContent = excerpt(paper.abstract);
    main.append(topline, title, abstract);

    if (paper.abstract.length > 420) {
      const details = document.createElement('details');
      const summary = document.createElement('summary');
      summary.textContent = 'Read full abstract';
      const full = document.createElement('p');
      full.className = 'paper-abstract';
      full.textContent = paper.abstract;
      details.append(summary, full);
      main.append(details);
    }

    if (paper.topics.length > 0) {
      const tags = document.createElement('div');
      tags.className = 'tag-row';
      for (const topic of paper.topics) {
        const tag = document.createElement('span');
        tag.className = 'tag';
        tag.textContent = topic;
        tags.append(tag);
      }
      main.append(tags);
    }

    const matches = document.createElement('div');
    matches.className = 'match-row';
    for (const match of ranking.matches.slice(0, 3)) {
      const chip = document.createElement('span');
      chip.className = 'match-chip';
      chip.textContent = `${match.label} · ${match.score.toFixed(2)}`;
      matches.append(chip);
    }
    main.append(matches);

    const score = document.createElement('div');
    score.className = 'paper-score';
    const presentation = paperScorePresentation(ranking);
    const caption = document.createElement('span');
    caption.className = 'paper-score-caption';
    caption.textContent = presentation.caption;
    const scoreValue = document.createElement('strong');
    scoreValue.textContent = ranking.score.toFixed(3);
    score.append(caption, scoreValue);
    for (const detail of presentation.details) {
      const line = document.createElement('span');
      line.className = 'paper-score-detail';
      line.textContent = detail;
      score.append(line);
    }
    if (ranking.rerankerProfileItemId) {
      const supportingItem = state.profileItems.find(
        (item) => item.id === ranking.rerankerProfileItemId,
      );
      if (supportingItem) {
        const line = document.createElement('span');
        line.className = 'paper-score-detail';
        line.textContent = `Strongest evidence: ${supportingItem.label}`;
        score.append(line);
      }
    }
    const explanation = describePreferenceFeedback(ranking, state.papers);
    if (explanation) {
      const tooltipId = `feedback-tip-${paper.id}`;
      score.classList.add('has-feedback-tip');
      score.tabIndex = 0;
      score.setAttribute(
        'aria-label',
        [ranking.score.toFixed(3), presentation.caption, ...presentation.details]
          .join('. ')
          .trim(),
      );
      score.setAttribute('aria-describedby', tooltipId);
      const tooltip = document.createElement('p');
      tooltip.id = tooltipId;
      tooltip.className = 'feedback-tooltip';
      tooltip.role = 'tooltip';
      tooltip.textContent = explanation;
      score.append(tooltip);
    }

    card.append(main, score, decisionControls(paper.id));
    fragment.append(card);
  }
  resultList.append(fragment);
}

function auditChoiceLabel(choice: ChoiceAuditEntry['choice']): string {
  switch (choice) {
    case 'interested':
      return 'Interested';
    case 'uncertain':
      return 'Would review if needed';
    case 'not_interested':
      return 'Not interested';
    default:
      return 'Unmapped preference';
  }
}

function formatOrdinal(value: number): string {
  const remainder100 = value % 100;
  const suffix =
    remainder100 >= 11 && remainder100 <= 13
      ? 'th'
      : value % 10 === 1
        ? 'st'
        : value % 10 === 2
          ? 'nd'
          : value % 10 === 3
            ? 'rd'
            : 'th';
  return `${value}${suffix}`;
}

function auditMetric(label: string, value: number, tone?: string): HTMLDivElement {
  const metric = document.createElement('div');
  metric.className = `audit-metric${tone ? ` ${tone}` : ''}`;
  const count = document.createElement('strong');
  count.textContent = value.toString();
  const caption = document.createElement('span');
  caption.textContent = label;
  metric.append(count, caption);
  return metric;
}

function auditChoiceCard(
  entry: ChoiceAuditEntry,
  ranking: RankingResult | undefined,
  includeControls: boolean,
): HTMLElement {
  const card = document.createElement('article');
  card.className = `audit-choice-card ${entry.outcome}`;
  card.dataset.auditPaper = entry.paper.id;

  const body = document.createElement('div');
  body.className = 'audit-choice-main';
  const topline = document.createElement('div');
  topline.className = 'paper-topline';
  const source =
    entry.source === 'local'
      ? 'choice made here'
      : `imported preference${entry.importedValue ? ` ${entry.importedValue}` : ''}`;
  topline.textContent = `${auditChoiceLabel(entry.choice)} · ${source} · Paper ${entry.paper.id}`;
  const title = document.createElement('h4');
  title.textContent = entry.paper.title;
  const reason = document.createElement('p');
  reason.className = 'audit-reason';
  reason.textContent = entry.reason;
  body.append(topline, title, reason);

  if (ranking && ranking.matches.length > 0) {
    const matches = document.createElement('div');
    matches.className = 'match-row';
    for (const match of ranking.matches.slice(0, 3)) {
      const chip = document.createElement('span');
      chip.className = 'match-chip';
      chip.textContent = `${match.label} · ${match.score.toFixed(2)}`;
      matches.append(chip);
    }
    body.append(matches);
  }

  const evidence = document.createElement('div');
  evidence.className = 'audit-evidence';
  const percentile = document.createElement('strong');
  percentile.textContent =
    entry.percentile === undefined ? '—' : formatOrdinal(Math.round(entry.percentile * 100));
  const percentileLabel = document.createElement('span');
  percentileLabel.textContent = 'Profile percentile';
  evidence.append(percentile, percentileLabel);
  if (entry.baselineScore !== undefined) {
    const score = document.createElement('small');
    score.textContent = `Independent score ${entry.baselineScore.toFixed(3)}`;
    evidence.append(score);
  }
  const openPaper = button('Open in review', 'button secondary');
  openPaper.dataset.openReviewPaper = entry.paper.id;
  evidence.append(openPaper);
  card.append(body, evidence);
  if (includeControls) card.append(decisionControls(entry.paper.id));
  return card;
}

function renderProfileSuggestion(suggestion: ProfileSuggestion, index: number): HTMLElement {
  const card = document.createElement('article');
  card.className = 'profile-suggestion-card';
  const label = document.createElement('span');
  label.className = 'suggestion-kind';
  label.textContent =
    suggestion.kind === 'add_area'
      ? 'Possible missing area'
      : suggestion.kind === 'increase_weight'
        ? 'Consider more emphasis'
        : 'Consider less emphasis';
  const title = document.createElement('h4');
  const reason = document.createElement('p');
  const action = button('', 'button secondary');
  action.dataset.profileSuggestion = index.toString();
  if (suggestion.kind === 'add_area') {
    title.textContent = suggestion.label;
    reason.textContent = suggestion.reason;
    action.textContent = 'Draft profile item';
  } else {
    title.textContent = suggestion.profileItem.label;
    reason.textContent = `${suggestion.reason} Current weight ${suggestion.profileItem.weight.toFixed(1)}; suggested ${suggestion.suggestedWeight.toFixed(1)}.`;
    action.textContent = 'Review profile weight';
  }
  card.append(label, title, reason, action);
  return card;
}

function renderChoiceCheck(): void {
  const audit = auditChoices({
    papers: state.papers,
    rankings: state.rankings,
    decisions: state.decisions,
    preferenceMapping: state.preferenceMapping,
    paperEmbeddings: state.paperEmbeddings,
    profileItems: state.profileItems,
    profileEmbeddings: state.profileEmbeddings,
  });
  choiceCheckSummary.replaceChildren();
  choiceReviewList.replaceChildren();
  choiceConsistentList.replaceChildren();
  choiceAlternatives.replaceChildren();
  profileSuggestions.replaceChildren();

  if (state.papers.length === 0) {
    choiceCheckContent.hidden = true;
    setText(choiceCheckStatus, 'Import papers before checking previous choices.');
    return;
  }
  if (state.rankings.length === 0) {
    choiceCheckContent.hidden = true;
    setText(choiceCheckStatus, 'Generate local rankings to compare choices with your current profile.');
    return;
  }
  const choiceCount = audit.consistent.length + audit.review.length;
  if (choiceCount === 0) {
    choiceCheckContent.hidden = true;
    setText(
      choiceCheckStatus,
      audit.conflicts.length > 0
        ? `${audit.conflicts.length} conflict choice${audit.conflicts.length === 1 ? ' is' : 's are'} excluded from topical analysis. Add or import a directional preference to run the check.`
        : 'No previous Interested, Would review, or Not interested choices were found.',
    );
    return;
  }

  choiceCheckContent.hidden = false;
  setText(
    choiceCheckStatus,
    `${audit.confidenceMessage}${audit.conflicts.length > 0 ? ` ${audit.conflicts.length} conflict choice${audit.conflicts.length === 1 ? ' is' : 's are'} excluded.` : ''}`,
  );
  choiceCheckSummary.append(
    auditMetric('Choices checked', choiceCount),
    auditMetric('Look consistent', audit.consistent.length, 'positive'),
    auditMetric('Worth another look', audit.review.length, 'attention'),
    auditMetric(
      'Better-fit papers',
      audit.alternatives.reduce((total, group) => total + group.alternatives.length, 0),
    ),
  );
  choiceConsistentCount.textContent = `${audit.consistent.length} choice${audit.consistent.length === 1 ? '' : 's'}`;
  const rankByPaper = new Map(state.rankings.map((ranking) => [ranking.paperId, ranking]));
  for (const entry of audit.review) {
    choiceReviewList.append(auditChoiceCard(entry, rankByPaper.get(entry.paper.id), true));
  }
  if (audit.review.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'audit-empty';
    empty.textContent = 'No choices fall outside their expected profile-fit range.';
    choiceReviewList.append(empty);
  }
  for (const entry of audit.consistent) {
    choiceConsistentList.append(auditChoiceCard(entry, rankByPaper.get(entry.paper.id), true));
  }

  const alternativeGroups = audit.alternatives.filter((group) => group.alternatives.length > 0);
  for (const group of alternativeGroups) {
    const section = document.createElement('article');
    section.className = 'alternative-group';
    const heading = document.createElement('h4');
    heading.textContent = `Instead of “${group.choice.paper.title}”`;
    section.append(heading);
    const grid = document.createElement('div');
    grid.className = 'alternative-grid';
    for (const alternative of group.alternatives) {
      const card = document.createElement('div');
      card.className = 'alternative-card';
      const title = document.createElement('strong');
      title.textContent = alternative.paper.title;
      const detail = document.createElement('p');
      detail.textContent =
        `${formatOrdinal(Math.round(alternative.percentile * 100))} profile percentile · ` +
        `${Math.round(alternative.percentileImprovement * 100)} points higher` +
        (alternative.similarityToChoice === undefined
          ? ''
          : ` · ${(alternative.similarityToChoice * 100).toFixed(0)}% similar to this choice`);
      const matches = document.createElement('p');
      matches.className = 'muted';
      matches.textContent =
        alternative.matches.length > 0
          ? `Matches: ${alternative.matches.map((match) => match.label).join(', ')}`
          : 'No profile explanation is available.';
      const open = button('Review this paper', 'button secondary');
      open.dataset.openReviewPaper = alternative.paper.id;
      card.append(title, detail, matches, open);
      grid.append(card);
    }
    section.append(grid);
    choiceAlternatives.append(section);
  }
  if (alternativeGroups.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'audit-empty';
    empty.textContent = 'No clearly stronger unselected profile matches were found.';
    choiceAlternatives.append(empty);
  }

  audit.profileSuggestions.forEach((suggestion, index) => {
    profileSuggestions.append(renderProfileSuggestion(suggestion, index));
  });
  if (audit.profileSuggestions.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'audit-empty';
    empty.textContent =
      'There is not enough consistent evidence to suggest a profile change yet. More manual choices may reveal useful patterns.';
    profileSuggestions.append(empty);
  }
}

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

function svgElement<K extends keyof SVGElementTagNameMap>(name: K): SVGElementTagNameMap[K] {
  return document.createElementNS(SVG_NAMESPACE, name);
}

function publicationProfileItems() {
  return state.profileItems.filter((item) => item.kind === 'publication');
}

function renderPublicationPicker(): void {
  const publications = publicationProfileItems();
  const validIds = new Set(publications.map((item) => item.id));
  state.selectedPublicationIds = state.selectedPublicationIds.filter((id) => validIds.has(id));
  similarityPublications.replaceChildren();
  if (publications.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'muted';
    empty.textContent = 'No publication items yet. Add publication text or extract a PDF in Step 1.';
    similarityPublications.append(empty);
    return;
  }

  for (const item of publications) {
    const label = document.createElement('label');
    label.className = 'publication-option';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = state.selectedPublicationIds.includes(item.id);
    checkbox.dataset.publicationId = item.id;
    const content = document.createElement('span');
    const title = document.createElement('strong');
    title.textContent = item.label;
    const detail = document.createElement('small');
    detail.textContent = `Weight ${item.weight.toFixed(1)} · ${excerpt(item.text, 90)}`;
    content.append(title, detail);
    label.append(checkbox, content);
    similarityPublications.append(label);
  }
}

function renderSimilarity(): void {
  renderPublicationPicker();
  similarityLimit.value = state.similarityLimit.toString();
  similarityHideVotedPapers.checked = hideVotedPapers;
  similarityMap.replaceChildren();
  similarityResults.replaceChildren();

  const selected = publicationProfileItems().filter((item) =>
    state.selectedPublicationIds.includes(item.id),
  );
  if (selected.length === 0) {
    setText(similarityStatus, 'Select at least one publication to define this interest cluster.');
    setText(similarityCount, 'No similarity results yet.');
    return;
  }
  if (state.papers.length === 0) {
    setText(similarityStatus, 'Import conference papers before building the similarity map.');
    setText(similarityCount, 'No similarity results yet.');
    return;
  }
  const visiblePapers = hideVotedPapers
    ? state.papers.filter((paper) => state.decisions[paper.id]?.status === undefined)
    : state.papers;
  if (visiblePapers.length === 0) {
    setText(
      similarityStatus,
      'All imported papers have been voted on. Turn off “Hide voted papers” to show them.',
    );
    setText(similarityCount, 'No unreviewed similarity results.');
    return;
  }
  if (
    selected.some((item) => !state.profileEmbeddings[item.id]) ||
    Object.keys(state.paperEmbeddings).length === 0
  ) {
    setText(
      similarityStatus,
      'Generate rankings after choosing your profile publications so their local embeddings are available.',
    );
    setText(similarityCount, 'No similarity results yet.');
    return;
  }

  const rankings = rankByPublicationCluster({
    profileItems: state.profileItems,
    selectedPublicationIds: state.selectedPublicationIds,
    profileEmbeddings: state.profileEmbeddings,
    profileBodyEmbeddings: state.profileBodyEmbeddings,
    papers: visiblePapers,
    paperEmbeddings: state.paperEmbeddings,
    limit: state.similarityLimit,
  });
  const papersById = new Map(state.papers.map((paper) => [paper.id, paper]));
  const rows = rankings
    .map((ranking) => ({ ranking, paper: papersById.get(ranking.paperId) }))
    .filter((row): row is { ranking: RankingResult; paper: Paper } => row.paper !== undefined);

  setText(
    similarityStatus,
    `Comparing ${visiblePapers.length} ${hideVotedPapers ? 'unreviewed ' : ''}submissions with ${selected.length} selected publication${selected.length === 1 ? '' : 's'}. Distances are a two-dimensional view of the local semantic embeddings.`,
  );
  setText(
    similarityCount,
    `Showing ${rows.length} most similar paper${rows.length === 1 ? '' : 's'}.`,
  );

  const projectionInputs = [
    ...selected.map((item) => ({
      id: `profile:${item.id}`,
      embedding: state.profileEmbeddings[item.id]!,
    })),
    ...rows.map(({ paper }) => ({
      id: `paper:${paper.id}`,
      embedding: state.paperEmbeddings[paper.id]!,
    })),
  ];
  const projected = projectEmbeddings2d(projectionInputs);
  const labels = new Map<string, string>([
    ...selected.map((item): [string, string] => [`profile:${item.id}`, item.label]),
    ...rows.map(
      ({ paper }, index): [string, string] => [
        `paper:${paper.id}`,
        `#${index + 1} ${paper.title}`,
      ],
    ),
  ]);

  for (let grid = 1; grid < 4; grid += 1) {
    const position = 55 + grid * (790 / 4);
    const vertical = svgElement('line');
    vertical.setAttribute('x1', position.toString());
    vertical.setAttribute('x2', position.toString());
    vertical.setAttribute('y1', '45');
    vertical.setAttribute('y2', '475');
    vertical.classList.add('map-gridline');
    similarityMap.append(vertical);
  }

  for (const point of projected) {
    const x = 55 + ((point.x + 1) / 2) * 790;
    const y = 45 + (1 - (point.y + 1) / 2) * 430;
    const group = svgElement('g');
    const isProfile = point.id.startsWith('profile:');
    group.classList.add('map-point', isProfile ? 'map-seed' : 'map-paper');
    group.setAttribute('transform', `translate(${x} ${y})`);
    group.setAttribute('tabindex', '0');
    group.setAttribute('role', isProfile ? 'img' : 'button');
    group.setAttribute('aria-label', labels.get(point.id) ?? point.id);
    const title = svgElement('title');
    title.textContent = labels.get(point.id) ?? point.id;
    group.append(title);
    if (isProfile) {
      const diamond = svgElement('path');
      diamond.setAttribute('d', 'M 0 -10 L 10 0 L 0 10 L -10 0 Z');
      group.append(diamond);
    } else {
      const paperId = point.id.slice('paper:'.length);
      group.dataset.paperId = paperId;
      const status = applyReviewStatus(group, paperId);
      if (status) {
        group.setAttribute(
          'aria-label',
          `${labels.get(point.id) ?? point.id} · ${reviewStatusLabel(status)}`,
        );
      }
      const circle = svgElement('circle');
      circle.setAttribute('r', '8');
      const label = svgElement('text');
      label.setAttribute('x', '12');
      label.setAttribute('y', '4');
      label.textContent = `#${rows.findIndex(({ paper }) => paper.id === paperId) + 1}`;
      group.append(circle, label);
    }
    similarityMap.append(group);
  }

  for (const [{ paper, ranking }, index] of rows.map((row, rowIndex) => [row, rowIndex] as const)) {
    const card = document.createElement('article');
    card.className = 'similarity-result';
    card.id = `similarity-paper-${paper.id}`;
    applyReviewStatus(card, paper.id);
    const top = document.createElement('div');
    top.className = 'similarity-result-top';
    const heading = document.createElement('h3');
    heading.textContent = `#${index + 1} ${paper.title}`;
    const score = document.createElement('strong');
    score.textContent = ranking.score.toFixed(3);
    top.append(heading, score);
    const metadata = document.createElement('p');
    metadata.className = 'muted';
    metadata.textContent = `Paper ${paper.id}${paper.track ? ` · ${paper.track}` : ''}`;
    const abstract = document.createElement('p');
    abstract.textContent = excerpt(paper.abstract, 180);
    const bar = document.createElement('div');
    bar.className = 'similarity-bar';
    const fill = document.createElement('span');
    fill.style.width = `${Math.max(0, Math.min(100, ((ranking.score + 1) / 2) * 100))}%`;
    bar.append(fill);
    const matches = document.createElement('div');
    matches.className = 'match-row';
    for (const match of ranking.matches.slice(0, 3)) {
      const chip = document.createElement('span');
      chip.className = 'match-chip';
      chip.textContent = `${match.label} · ${match.score.toFixed(2)}`;
      matches.append(chip);
    }
    card.append(top, metadata, abstract, bar, matches, decisionControls(paper.id));
    similarityResults.append(card);
  }
}

function renderMapping(): void {
  mapInterested.value = state.preferenceMapping.interested?.toString() ?? '';
  mapNotInterested.value = state.preferenceMapping.not_interested?.toString() ?? '';
  mapUncertain.value = state.preferenceMapping.uncertain?.toString() ?? '';
}

function renderAll(): void {
  persistenceToggle.checked = state.persistenceEnabled;
  setText(
    persistenceStatus,
    state.persistenceEnabled
      ? 'This session will be saved in this browser.'
      : 'Session is in memory only.',
  );
  renderProfile();
  renderImport();
  renderMapping();
  limitExport.checked = state.exportLimit !== null;
  exportLimit.disabled = state.exportLimit === null;
  exportLimit.value = (state.exportLimit ?? 10).toString();
  renderResults();
  renderSimilarity();
  renderChoiceCheck();
}

function importSource(source: string, fileName?: string): void {
  const parsed = parsePaperImport(source, fileName ? { fileName } : {});
  state.papers = parsed.papers;
  state.rankings = [];
  state.paperEmbeddings = {};
  state.decisions = {};
  setText(
    importErrors,
    parsed.issues
      .slice(0, 20)
      .map((issue) => `${issue.row ? `Row ${issue.row}: ` : ''}${issue.message}`)
      .join('\n'),
  );
  renderImport();
  renderResults();
  renderSimilarity();
  renderChoiceCheck();
  scheduleSave();
}

function mappingValue(input: HTMLInputElement): number | null {
  const value = input.value.trim();
  if (!value) return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) throw new Error('Preference mappings must be whole numbers or blank.');
  return parsed;
}

function readMapping(): PreferenceMapping {
  const mapping = {
    interested: mappingValue(mapInterested),
    not_interested: mappingValue(mapNotInterested),
    uncertain: mappingValue(mapUncertain),
  };
  validatePreferenceMapping(mapping);
  return mapping;
}

function rerankWithFeedback(baseRankings: readonly RankingResult[] = state.rankings): void {
  state.negativeKeywordEmbeddings = Object.fromEntries(
    state.negativeKeywords.flatMap((keyword) => {
      const embedding = state.negativeKeywordEmbeddings[keyword];
      return embedding ? [[keyword, embedding]] : [];
    }),
  );
  state.rankings = applyPreferenceFeedback(
    baseRankings,
    state.paperEmbeddings,
    state.decisions,
    state.negativeKeywordEmbeddings,
    { papers: state.papers },
  );
}

function startRanking(): void {
  if (activeRequestId) {
    setText(rankStatus, 'Local ranking is already in progress.');
    return;
  }
  if (state.profileItems.length === 0 || state.papers.length === 0) {
    setText(rankStatus, 'Add expertise and import at least one paper first.');
    return;
  }

  embeddingWorker ??= new Worker(new URL('./workers/embedding-worker.ts', import.meta.url), {
    type: 'module',
  });
  const requestId = crypto.randomUUID();
  activeRequestId = requestId;
  rankButton.disabled = true;
  cancelButton.disabled = false;
  rerankerCandidateCount.disabled = true;
  pdfSummaryOnly.disabled = true;
  rankProgress.value = 0;
  setText(rankStatus, 'Starting local model…');
  rerankerProgressWrap.hidden = !useReranker.checked;
  if (useReranker.checked) {
    rerankerProgress.value = 0;
    setText(rerankerStatus, 'Waiting to start Qwen3 reranker…');
  }

  embeddingWorker.onmessage = (event: MessageEvent<WorkerResponse>) => {
    const response = event.data;
    if (response.requestId !== activeRequestId) return;
    if (response.type === 'progress') {
      if (response.target === 'reranker') {
        rerankerProgressWrap.hidden = false;
        rerankerProgress.value = (response.value ?? 0) * 100;
        setText(rerankerStatus, response.message);
      } else {
        rankProgress.value = (response.value ?? 0) * 100;
        setText(rankStatus, response.message);
      }
      return;
    }

    rankButton.disabled = false;
    cancelButton.disabled = true;
    rerankerCandidateCount.disabled = false;
    pdfSummaryOnly.disabled = false;
    activeRequestId = null;
    if (response.type === 'complete') {
      state.profileEmbeddings = response.profileEmbeddings;
      state.profileBodyEmbeddings = response.profileBodyEmbeddings;
      state.paperEmbeddings = response.paperEmbeddings;
      state.negativeKeywordEmbeddings = response.negativeKeywordEmbeddings;
      rerankWithFeedback(response.rankings);
      rankProgress.value = 100;
      if (response.reranked) {
        rerankerProgress.value = 100;
        setText(rerankerStatus, 'Pairwise expertise reranking complete.');
      }
      const modelLabel = MODEL_DETAILS[response.modelId].label;
      setText(
        rankStatus,
        `Ranked ${response.rankings.length} papers locally with ${modelLabel} on ${response.device}${response.reranked ? `, then reranked ${response.rerankedCount} with Qwen3` : ''}.`,
      );
      setText(
        runtimeLabel,
        `Active model: ${modelLabel}${response.reranked ? ' + Qwen3 pairwise expertise reranker' : ''}; backend: ${response.device.toUpperCase()}.`,
      );
      renderResults();
      renderSimilarity();
      renderChoiceCheck();
      if (boostedPapersDialog.open) openBoostedPapersDialog();
      scheduleSave();
      if (
        state.negativeKeywords.some(
          (keyword) => response.negativeKeywordEmbeddings[keyword] === undefined,
        )
      ) {
        window.setTimeout(startRanking, 0);
      }
    } else if (response.type === 'cancelled') {
      setText(rankStatus, 'Ranking cancelled.');
      if (useReranker.checked) setText(rerankerStatus, 'Reranker cancelled.');
    } else {
      setText(rankStatus, `Ranking failed: ${response.message}`);
      if (useReranker.checked) setText(rerankerStatus, 'Reranker stopped.');
    }
  };

  const request: WorkerRequest = {
    type: 'rank',
    requestId,
    modelId: selectedEmbeddingModel(),
    useReranker: useReranker.checked,
    rerankerCandidateCount: selectedRerankerCandidateCount(),
    includePublicationBody: !pdfSummaryOnly.checked,
    profileItems: state.profileItems,
    papers: state.papers,
    negativeKeywords: state.negativeKeywords,
    preferredDevice: 'gpu' in navigator ? 'webgpu' : 'wasm',
  };
  embeddingWorker.postMessage(request);
}

profileForm.addEventListener('submit', (event) => {
  event.preventDefault();
  try {
    const existing = editingProfileId
      ? state.profileItems.find((item) => item.id === editingProfileId)
      : undefined;
    const updated = createProfileItem({
      ...(existing ? { id: existing.id } : {}),
      kind: profileKind.value as ProfileKind,
      label: profileLabel.value,
      text: profileText.value,
      weight: Number(profileWeight.value),
      ...(existing?.sourceFile ? { sourceFile: existing.sourceFile } : {}),
    });
    if (editingProfileId) {
      state.profileItems = state.profileItems.map((item) =>
        item.id === editingProfileId ? updated : item,
      );
    } else {
      state.profileItems.push(updated);
    }
    state.rankings = [];
    state.profileEmbeddings = {};
    state.profileBodyEmbeddings = {};
    editingProfileId = null;
    profileSubmit.textContent = 'Add profile item';
    profileLabel.value = '';
    profileText.value = '';
    profileWeight.value = '1';
    renderProfile();
    renderResults();
    renderSimilarity();
    renderChoiceCheck();
    scheduleSave();
  } catch (error) {
    setText(pdfStatus, error instanceof Error ? error.message : String(error));
  }
});

profileItems.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLButtonElement)) return;
  if (target.dataset.editProfile) {
    const item = state.profileItems.find((candidate) => candidate.id === target.dataset.editProfile);
    if (!item) return;
    editingProfileId = item.id;
    profileKind.value = item.kind;
    profileLabel.value = item.label;
    profileText.value = item.text;
    profileWeight.value = item.weight.toString();
    profileSubmit.textContent = 'Save profile item';
    profileLabel.focus();
    return;
  }
  if (target.dataset.removeProfile) {
    state.profileItems = state.profileItems.filter(
      (item) => item.id !== target.dataset.removeProfile,
    );
    state.rankings = [];
    state.profileEmbeddings = {};
    state.profileBodyEmbeddings = {};
    state.selectedPublicationIds = state.selectedPublicationIds.filter(
      (id) => id !== target.dataset.removeProfile,
    );
    if (editingProfileId === target.dataset.removeProfile) {
      editingProfileId = null;
      profileSubmit.textContent = 'Add profile item';
      profileForm.reset();
      profileWeight.value = '1';
    }
    renderProfile();
    renderResults();
    renderSimilarity();
    renderChoiceCheck();
    scheduleSave();
  }
});

pdfInput.addEventListener('change', () => {
  const files = [...(pdfInput.files ?? [])];
  if (files.length === 0) return;
  void (async () => {
    for (const [index, file] of files.entries()) {
      try {
        setText(pdfStatus, `Reading ${file.name} (${index + 1}/${files.length})…`);
        const text = await extractPdfText(file, ({ page, totalPages }) => {
          setText(pdfStatus, `Reading ${file.name}: page ${page} of ${totalPages}`);
        });
        state.profileItems.push(
          createProfileItem({
            kind: 'publication',
            label: file.name.replace(/\.pdf$/i, ''),
            text,
            weight: 1,
            sourceFile: file.name,
          }),
        );
        state.rankings = [];
        state.profileEmbeddings = {};
        state.profileBodyEmbeddings = {};
      } catch (error) {
        setText(
          pdfStatus,
          `${file.name}: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    }
    pdfInput.value = '';
    renderProfile();
    renderResults();
    renderSimilarity();
    renderChoiceCheck();
    scheduleSave();
    if (files.length > 0) setText(pdfStatus, `Finished processing ${files.length} PDF file(s).`);
  })();
});

pdfSummaryOnly.addEventListener('change', () => {
  localStorage.setItem(PDF_SUMMARY_ONLY_KEY, pdfSummaryOnly.checked.toString());
  state.rankings = [];
  state.profileEmbeddings = {};
  state.profileBodyEmbeddings = {};
  renderResults();
  renderSimilarity();
  renderChoiceCheck();
  setText(
    rankStatus,
    pdfSummaryOnly.checked
      ? 'Ready to rank uploaded publications from title and abstract only.'
      : 'Ready to rank uploaded publications with title, abstract, and body passages.',
  );
  scheduleSave();
});

paperFile.addEventListener('change', () => {
  const file = paperFile.files?.[0];
  if (!file) return;
  void file
    .text()
    .then((source) => importSource(source, file.name))
    .catch((error: unknown) =>
      setText(importErrors, `Could not read file: ${error instanceof Error ? error.message : String(error)}`),
    )
    .finally(() => {
      paperFile.value = '';
    });
});

element<HTMLButtonElement>('import-paste').addEventListener('click', () => {
  importSource(paperPaste.value);
});

rankButton.addEventListener('click', startRanking);
function resetSelectedModels(): void {
  const model = selectedEmbeddingModel();
  embeddingWorker?.terminate();
  embeddingWorker = null;
  activeRequestId = null;
  state.rankings = [];
  rankButton.disabled = false;
  cancelButton.disabled = true;
  rerankerCandidateCount.disabled = false;
  pdfSummaryOnly.disabled = false;
  rankProgress.value = 0;
  rerankerProgress.value = 0;
  rerankerProgressWrap.hidden = !useReranker.checked;
  setText(rerankerStatus, 'Reranker ready.');
  setText(modelDescription, MODEL_DETAILS[model].description);
  updateRuntimeLabel();
  setText(
    rankStatus,
    `Ready to load ${MODEL_DETAILS[model].label}${useReranker.checked ? ' and pairwise expertise reranking' : ''}.`,
  );
  renderResults();
  renderChoiceCheck();
}

embeddingModel.addEventListener('change', resetSelectedModels);
useReranker.addEventListener('change', resetSelectedModels);
rerankerCandidateCount.addEventListener('change', () => {
  const count = selectedRerankerCandidateCount();
  rerankerCandidateCount.value = count.toString();
  state.rankings = [];
  setText(
    rerankerEstimate,
    `Up to ${count} dense candidates will be reranked. A live estimate appears after the first candidate.`,
  );
  setText(rankStatus, `Ready to rerank up to ${count} candidates.`);
  renderResults();
  renderChoiceCheck();
});
cancelButton.addEventListener('click', () => {
  if (!embeddingWorker || !activeRequestId) return;
  embeddingWorker.postMessage({ type: 'cancel', requestId: activeRequestId } satisfies WorkerRequest);
  setText(rankStatus, 'Cancelling after the current local batch…');
});

let searchWasActive = false;
filterSearch.addEventListener('input', () => {
  const searchIsActive = filterSearch.value.trim().length > 0;
  if (searchIsActive && !searchWasActive) {
    sortResults.value = 'search-relevance';
  } else if (!searchIsActive && searchWasActive && sortResults.value === 'search-relevance') {
    sortResults.value = 'score-desc';
  }
  searchWasActive = searchIsActive;
  renderResults();
});

for (const control of [
  filterTopic,
  filterTrack,
  filterStatus,
  filterScore,
  sortResults,
]) {
  control.addEventListener('input', () => {
    scoreOutput.value = Number(filterScore.value).toFixed(2);
    renderResults();
  });
}

function setHideVotedPapers(hidden: boolean): void {
  hideVotedPapers = hidden;
  renderResults();
  renderSimilarity();
}

hideVotedPapersControl.addEventListener('change', () => {
  setHideVotedPapers(hideVotedPapersControl.checked);
});

similarityHideVotedPapers.addEventListener('change', () => {
  setHideVotedPapers(similarityHideVotedPapers.checked);
});

reviewTarget.addEventListener('change', () => {
  const raw = reviewTarget.value.trim();
  state.reviewTarget = raw === '' ? null : normalizeReviewTarget(Number(raw));
  renderReviewProgress();
  scheduleSave();
});

reviewStatusCounts.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const chip = target.closest<HTMLButtonElement>('button[data-status-filter]');
  const next = chip?.dataset.statusFilter;
  if (!next) return;
  filterStatus.value = filterStatus.value === next ? '' : next;
  renderResults();
});

similarityPublications.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.dataset.publicationId) return;
  const publicationId = target.dataset.publicationId;
  state.selectedPublicationIds = target.checked
    ? [...new Set([...state.selectedPublicationIds, publicationId])]
    : state.selectedPublicationIds.filter((id) => id !== publicationId);
  renderSimilarity();
  scheduleSave();
});

similarityLimit.addEventListener('change', () => {
  state.similarityLimit = normalizeSimilarityLimit(Number(similarityLimit.value));
  similarityLimit.max = MAX_SIMILARITY_LIMIT.toString();
  renderSimilarity();
  scheduleSave();
});

function highlightSimilarityPaper(paperId: string): void {
  for (const card of similarityResults.querySelectorAll('.similarity-result')) {
    card.classList.toggle('highlighted', card.id === `similarity-paper-${paperId}`);
  }
}

similarityMap.addEventListener('focusin', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const point = target.closest<SVGGElement>('[data-paper-id]');
  if (point?.dataset.paperId) highlightSimilarityPaper(point.dataset.paperId);
});

similarityMap.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const point = target.closest<SVGGElement>('[data-paper-id]');
  const paperId = point?.dataset.paperId;
  if (!paperId) return;
  highlightSimilarityPaper(paperId);
  document.getElementById(`similarity-paper-${paperId}`)?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
  });
});

function handleDecisionClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof HTMLButtonElement) || !target.dataset.paperId || !target.dataset.status) {
    return;
  }
  const paperId = target.dataset.paperId;
  const selected = target.dataset.status as ReviewStatus;
  setDecisionStatus(paperId, selected);
}

function handleDecisionInput(event: Event): void {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.dataset.notePaper) return;
  const paperId = target.dataset.notePaper;
  state.decisions[paperId] = {
    ...(state.decisions[paperId]?.status ? { status: state.decisions[paperId].status } : {}),
    note: target.value,
  };
  scheduleSave();
}

for (const container of [
  resultList,
  similarityResults,
  boostedPapersList,
  choiceReviewList,
  choiceConsistentList,
]) {
  container.addEventListener('click', handleDecisionClick);
  container.addEventListener('input', handleDecisionInput);
}

function openPaperInReview(paperId: string): void {
  filterSearch.value = '';
  filterTopic.value = '';
  filterTrack.value = '';
  filterStatus.value = '';
  filterScore.value = '-1';
  scoreOutput.value = '-1.00';
  renderResults();
  const card = [...resultList.querySelectorAll<HTMLElement>('[data-result-paper]')].find(
    (candidate) => candidate.dataset.resultPaper === paperId,
  );
  card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  card?.focus({ preventScroll: true });
}

function draftProfileSuggestion(suggestion: ProfileSuggestion): void {
  if (suggestion.kind === 'add_area') {
    editingProfileId = null;
    profileForm.reset();
    profileKind.value = 'interest';
    profileLabel.value = suggestion.label;
    profileText.value = suggestion.text;
    profileWeight.value = '1';
    profileSubmit.textContent = 'Add profile item';
  } else {
    const item = state.profileItems.find(
      (candidate) => candidate.id === suggestion.profileItem.id,
    );
    if (!item) return;
    editingProfileId = item.id;
    profileKind.value = item.kind;
    profileLabel.value = item.label;
    profileText.value = item.text;
    profileWeight.value = suggestion.suggestedWeight.toString();
    profileSubmit.textContent = 'Save profile item';
  }
  document.querySelector('#profile')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  profileLabel.focus({ preventScroll: true });
}

choiceCheckContent.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const open = target.closest<HTMLButtonElement>('button[data-open-review-paper]');
  if (open?.dataset.openReviewPaper) {
    openPaperInReview(open.dataset.openReviewPaper);
    return;
  }
  const profileAction = target.closest<HTMLButtonElement>('button[data-profile-suggestion]');
  if (!profileAction?.dataset.profileSuggestion) return;
  const index = Number(profileAction.dataset.profileSuggestion);
  const audit = auditChoices({
    papers: state.papers,
    rankings: state.rankings,
    decisions: state.decisions,
    preferenceMapping: state.preferenceMapping,
    paperEmbeddings: state.paperEmbeddings,
    profileItems: state.profileItems,
    profileEmbeddings: state.profileEmbeddings,
  });
  const suggestion = audit.profileSuggestions[index];
  if (suggestion) draftProfileSuggestion(suggestion);
});

negativeKeywordForm.addEventListener('submit', (event) => {
  event.preventDefault();
  const keyword = negativeKeyword.value.trim().toLocaleLowerCase();
  if (!keyword || state.negativeKeywords.includes(keyword)) return;
  addNegativeKeywords([keyword]);
  negativeKeyword.value = '';
  renderNegativeKeywords();
  scheduleSave();
  if (state.profileItems.length > 0 && state.papers.length > 0) startRanking();
});

negativeKeywords.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLButtonElement) || !target.dataset.removeNegativeKeyword) return;
  const keyword = target.dataset.removeNegativeKeyword;
  state.negativeKeywords = state.negativeKeywords.filter((candidate) => candidate !== keyword);
  delete state.negativeKeywordEmbeddings[keyword];
  rerankWithFeedback();
  renderNegativeKeywords();
  renderResults();
  renderChoiceCheck();
  scheduleSave();
});

negativeKeyword.addEventListener('input', () => {
  setText(
    rankStatus,
    negativeKeyword.value.trim()
      ? 'Negative phrases use semantic similarity after you add them.'
      : 'Ready.',
  );
});

for (const input of [mapInterested, mapNotInterested, mapUncertain]) {
  input.addEventListener('change', () => {
    try {
      state.preferenceMapping = readMapping();
      setText(exportStatus, 'Preference mapping updated.');
      renderChoiceCheck();
      scheduleSave();
    } catch (error) {
      setText(exportStatus, error instanceof Error ? error.message : String(error));
    }
  });
}

function readExportLimit(): number {
  const value = Number(exportLimit.value);
  if (!Number.isInteger(value) || value < 1) {
    throw new Error('Top-choice export limit must be a positive whole number.');
  }
  return value;
}

limitExport.addEventListener('change', () => {
  exportLimit.disabled = !limitExport.checked;
  try {
    state.exportLimit = limitExport.checked ? readExportLimit() : null;
    setText(
      exportStatus,
      state.exportLimit === null
        ? 'HotCRP export will include all non-conflict papers.'
        : `HotCRP export will include up to ${state.exportLimit} interested choices.`,
    );
    scheduleSave();
  } catch (error) {
    limitExport.checked = false;
    exportLimit.disabled = true;
    state.exportLimit = null;
    setText(exportStatus, error instanceof Error ? error.message : String(error));
  }
});

exportLimit.addEventListener('change', () => {
  if (!limitExport.checked) return;
  try {
    state.exportLimit = readExportLimit();
    setText(exportStatus, `HotCRP export limited to ${state.exportLimit} interested choices.`);
    scheduleSave();
  } catch (error) {
    setText(exportStatus, error instanceof Error ? error.message : String(error));
  }
});

persistenceToggle.addEventListener('change', () => {
  void (async () => {
    if (persistenceToggle.checked) {
      state.persistenceEnabled = true;
      localStorage.setItem(CONSENT_KEY, 'true');
      await savePersistedState(state, true);
      setText(persistenceStatus, 'Saved locally in this browser.');
    } else {
      await clearPersistedState(true);
      state.persistenceEnabled = false;
      localStorage.setItem(CONSENT_KEY, 'false');
      setText(persistenceStatus, 'Saved copy removed. Session is in memory only.');
    }
  })().catch((error: unknown) => {
    persistenceToggle.checked = state.persistenceEnabled;
    setText(
      persistenceStatus,
      `Persistence change failed: ${error instanceof Error ? error.message : String(error)}`,
    );
  });
});

element<HTMLButtonElement>('export-session').addEventListener('click', () => {
  try {
    const date = new Date().toISOString().slice(0, 10);
    downloadJson(exportPersistedState(state), `paper-bidding-preferences-${date}.json`);
    setText(persistenceStatus, 'Exported reviewing preferences and session data.');
  } catch (error) {
    setText(
      persistenceStatus,
      `Could not export preferences: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
});

sessionImport.addEventListener('change', () => {
  const file = sessionImport.files?.[0];
  if (!file) return;
  void file
    .text()
    .then(async (source) => {
      const imported = importPersistedState(source);
      const persistenceEnabled = state.persistenceEnabled;
      state = { ...imported, persistenceEnabled };
      rerankWithFeedback();
      editingProfileId = null;
      profileSubmit.textContent = 'Add profile item';
      renderAll();
      if (persistenceEnabled) {
        await savePersistedState(state, true);
        setText(persistenceStatus, 'Imported preferences and saved them in this browser.');
      } else {
        setText(persistenceStatus, 'Imported preferences into memory. Browser saving remains off.');
      }
    })
    .catch((error: unknown) => {
      setText(
        persistenceStatus,
        `Could not import preferences: ${error instanceof Error ? error.message : String(error)}`,
      );
    })
    .finally(() => {
      sessionImport.value = '';
    });
});

element<HTMLButtonElement>('export-personal').addEventListener('click', () => {
  if (state.papers.length === 0) {
    setText(exportStatus, 'Import papers before exporting.');
    return;
  }
  const result = createPersonalCsv(state.papers, state.decisions, state.rankings);
  downloadCsv(result.csv, 'paper-bidding-personal.csv');
  setText(exportStatus, `Exported ${result.summary.exportedRows} personal review rows.`);
});

element<HTMLButtonElement>('export-hotcrp').addEventListener('click', () => {
  try {
    state.preferenceMapping = readMapping();
    if (limitExport.checked) state.exportLimit = readExportLimit();
    const papersToExport =
      state.exportLimit === null
        ? state.papers
        : selectTopInterestedPapers(
            state.papers,
            state.decisions,
            state.rankings,
            state.exportLimit,
          );
    const result = createHotcrpCsv(papersToExport, state.decisions, state.preferenceMapping);
    if (result.summary.exportedRows === 0) {
      setText(
        exportStatus,
        state.exportLimit === null
          ? 'No non-conflict papers are available to export.'
          : 'Mark at least one non-excluded paper as interested before using a top-choice limit.',
      );
      return;
    }
    downloadCsv(result.csv, 'hotcrp-review-preferences.csv');
    setText(
      exportStatus,
      `Exported ${result.summary.exportedRows}${state.exportLimit === null ? '' : ` of up to ${state.exportLimit} top interested choices`}; mapped ${result.summary.mappedPreferences}, preserved ${result.summary.untouchedPreferences}, omitted ${result.summary.omittedConflicts} conflicts.`,
    );
    scheduleSave();
  } catch (error) {
    setText(exportStatus, error instanceof Error ? error.message : String(error));
  }
});

element<HTMLButtonElement>('clear-data').addEventListener('click', () => {
  element<HTMLHeadingElement>('dialog-title').textContent = 'Clear all local data?';
  element<HTMLParagraphElement>('dialog-message').textContent =
    'This removes the profile, imported papers, rankings, statuses, notes, and saved browser copy.';
  confirmDialog.showModal();
});

confirmDialog.addEventListener('close', () => {
  if (confirmDialog.returnValue !== 'confirm') return;
  void clearPersistedState(true)
    .catch(() => undefined)
    .finally(() => {
      localStorage.setItem(CONSENT_KEY, 'true');
      embeddingWorker?.terminate();
      embeddingWorker = null;
      activeRequestId = null;
      editingProfileId = null;
      state = {
        profileItems: [],
        papers: [],
        rankings: [],
        profileEmbeddings: {},
        profileBodyEmbeddings: {},
        paperEmbeddings: {},
        negativeKeywordEmbeddings: {},
        decisions: {},
        preferenceMapping: { ...DEFAULT_MAPPING },
        negativeKeywords: [],
        exportLimit: null,
        selectedPublicationIds: [],
        similarityLimit: 20,
        reviewTarget: null,
        persistenceEnabled: true,
      };
      renderAll();
      setText(rankStatus, 'All session data cleared.');
    });
});

async function restore(): Promise<void> {
  if (!state.persistenceEnabled) {
    renderAll();
    return;
  }
  try {
    const saved = await loadPersistedState(true);
    if (saved) {
      state = { ...saved, persistenceEnabled: true };
      rerankWithFeedback();
    }
  } catch (error) {
    localStorage.setItem(CONSENT_KEY, 'false');
    state.persistenceEnabled = false;
    setText(
      persistenceStatus,
      `Saved state could not be restored: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
  renderAll();
}

updateRuntimeLabel();
void restore();
